/**
 * Hooks de datos del servidor.
 *
 * **TanStack Query es el estado del servidor.** No se copian estos datos a
 * `useState` ni a un Context: en cuanto hay dos copias, una se queda vieja y el bug
 * aparece como "a veces se ve un número distinto".
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { setSesion } from "@/api/auth"
import { request } from "@/api/client"
import type {
  Agrupacion,
  Alias,
  Aplicacion,
  Clave,
  ClaveEmitida,
  Consumo,
  CambiarEstadoApp,
  CrearAplicacion,
  CrearClave,
  CrearOperador,
  CrearPrecio,
  Credencial,
  GuardarAlias,
  Operador,
  OperadorCreado,
  Organizacion,
  PasoTraza,
  GuardarPresupuesto,
  Periodo,
  Precio,
  Presupuesto,
  Reconciliacion,
  Rol,
  Serie,
  Sesion,
  Yo,
} from "@/api/types"

export const claves = {
  apps: (dias: number) => ["apps", dias] as const,
  clavesDe: (appId: string) => ["keys", appId] as const,
  aliases: () => ["aliases"] as const,
  precios: (alias?: string) => ["prices", alias ?? "todos"] as const,
  resumen: (dias: number, appId?: string) => ["usage", dias, appId ?? "todas"] as const,
  diario: (dias: number, appId?: string) => ["usage-daily", dias, appId ?? "todas"] as const,
  agrupado: (por: Agrupacion, dias: number, appId?: string) =>
    ["usage-by", por, dias, appId ?? "todas"] as const,
  traza: (id: string) => ["trace", id] as const,
  yo: () => ["me"] as const,
  organizacion: () => ["organization"] as const,
  operadores: () => ["operators"] as const,
  credenciales: () => ["credentials"] as const,
  reconciliaciones: (limite: number) => ["reconciliations", limite] as const,
  presupuestos: (appId: string) => ["budgets", appId] as const,
}

function query(path: string, params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

// ---------------------------------------------------------------- sesión

export function useEntrar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (datos: { email: string; password: string }) =>
      request<Sesion>("/admin/auth/login", { method: "POST", body: datos }),
    onSuccess: (sesion) => {
      setSesion(sesion.access_token, sesion.expires_in)
      // La caché es de la sesión anterior. No vaciarla haría que quien entra después
      // viera un instante los datos de quien entró antes — en la misma pestaña, con
      // otro rol y puede que de otra organización.
      qc.clear()
    },
  })
}

/**
 * Quién soy y qué puedo hacer.
 *
 * La lista de permisos viene del gateway en vez de derivarse del rol aquí: la tabla de
 * roles vive en un sitio y solo en uno.
 */
export function useYo() {
  return useQuery({
    queryKey: claves.yo(),
    queryFn: () => request<Yo>("/admin/auth/me"),
    // Se reintenta poco: si esto falla con 401, lo que toca es volver a entrar, no
    // insistir. El cliente ya ha limpiado la sesión para cuando llega aquí.
    retry: false,
  })
}

export function useCambiarPassword() {
  return useMutation({
    mutationFn: (datos: { actual: string; nueva: string }) =>
      request<void>("/admin/auth/password", { method: "PUT", body: datos }),
  })
}

// ---------------------------------------------------------------- organización

export function useOrganizacion() {
  return useQuery({
    queryKey: claves.organizacion(),
    queryFn: () => request<Organizacion>("/admin/organization"),
  })
}

// ---------------------------------------------------------------- personas

export function useOperadores() {
  return useQuery({
    queryKey: claves.operadores(),
    queryFn: () => request<Operador[]>("/admin/operators"),
  })
}

export function useCrearOperador() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (datos: CrearOperador) =>
      request<OperadorCreado>("/admin/operators", { method: "POST", body: datos }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.operadores() }),
  })
}

export function useCambiarRol() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ operatorId, role }: { operatorId: string; role: Rol }) =>
      request<void>(`/admin/operators/${operatorId}/role`, {
        method: "PUT",
        body: { role },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.operadores() }),
  })
}

export function useDesactivarOperador() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (operatorId: string) =>
      request<void>(`/admin/operators/${operatorId}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.operadores() }),
  })
}

// ---------------------------------------------------------------- credenciales BYOK

export function useCredenciales() {
  return useQuery({
    queryKey: claves.credenciales(),
    queryFn: () => request<Credencial[]>("/admin/credentials"),
  })
}

export function useGuardarCredencial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ providerId, apiKey }: { providerId: string; apiKey: string }) =>
      request<{ provider_id: string; estado: string }>(`/admin/credentials/${providerId}`, {
        method: "PUT",
        body: { api_key: apiKey },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.credenciales() }),
  })
}

export function useBorrarCredencial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (providerId: string) =>
      request<void>(`/admin/credentials/${providerId}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.credenciales() }),
  })
}

// ---------------------------------------------------------------- aplicaciones

export function useAplicaciones(dias: number) {
  return useQuery({
    queryKey: claves.apps(dias),
    queryFn: () => request<Aplicacion[]>(query("/admin/apps", { dias })),
  })
}

export function useCrearAplicacion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (datos: CrearAplicacion) =>
      request<{ app_id: string; slug: string }>("/admin/apps", { method: "POST", body: datos }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["apps"] }),
  })
}

/**
 * Suspende o reactiva una aplicación.
 *
 * Invalida también la lista de aplicaciones, no solo el detalle: el estado se pinta en
 * las dos, y dejar la lista vieja hace dudar de si el cambio se aplicó.
 */
export function useCambiarEstadoApp(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (datos: CambiarEstadoApp) =>
      request<{ app_id: string; status: string }>(`/admin/apps/${appId}/status`, {
        method: "PUT",
        body: datos,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["apps"] }),
  })
}

export function useClaves(appId: string) {
  return useQuery({
    queryKey: claves.clavesDe(appId),
    queryFn: () => request<Clave[]>(`/admin/apps/${appId}/keys`),
    enabled: appId !== "",
  })
}

export function useEmitirClave(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (datos: CrearClave) =>
      request<ClaveEmitida>(`/admin/apps/${appId}/keys`, { method: "POST", body: datos }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.clavesDe(appId) }),
  })
}

export function useRevocarClave(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (keyId: string) => request<void>(`/admin/keys/${keyId}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.clavesDe(appId) }),
  })
}

/**
 * Los presupuestos de una aplicación, con lo gastado y lo reservado.
 *
 * `VerConsumo` basta para mirarlos: quien paga la factura tiene que poder ver si se
 * está acercando al tope sin poder moverlo.
 */
/**
 * ¿Puede quien está dentro hacer esto?
 *
 * Contra la lista de permisos de `/admin/auth/me`, **nunca contra el rol**: comparar
 * roles aquí sería reimplementar la tabla de permisos en el navegador, y dos tablas
 * acaban diciendo cosas distintas — que es como aparece un botón que siempre da 403.
 *
 * Esconder el botón es cortesía, no seguridad: quien mande la petición igual se topa
 * con el permiso en el servidor, que es donde se decide.
 */
export function usePuede(permiso: string): boolean {
  const yo = useYo()
  return (yo.data?.permisos ?? []).includes(permiso)
}

export function usePresupuestos(appId: string) {
  return useQuery({
    queryKey: claves.presupuestos(appId),
    queryFn: () => request<Presupuesto[]>(`/admin/apps/${appId}/budgets`),
    enabled: appId !== "",
  })
}

export function useGuardarPresupuesto(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ periodo, datos }: { periodo: Periodo; datos: GuardarPresupuesto }) =>
      request<{ app_id: string; period: string }>(`/admin/apps/${appId}/budgets/${periodo}`, {
        method: "PUT",
        body: datos,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.presupuestos(appId) }),
  })
}

/** Quitar el presupuesto deja la aplicación **sin límite**, no con límite cero. */
export function useBorrarPresupuesto(appId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (periodo: Periodo) =>
      request<void>(`/admin/apps/${appId}/budgets/${periodo}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.presupuestos(appId) }),
  })
}

export function useResumen(dias: number, appId?: string) {
  return useQuery({
    queryKey: claves.resumen(dias, appId),
    queryFn: () => request<Consumo>(query("/admin/usage", { dias, app_id: appId })),
  })
}

export function useConsumoDiario(dias: number, appId?: string) {
  return useQuery({
    queryKey: claves.diario(dias, appId),
    queryFn: () => request<Serie[]>(query("/admin/usage/daily", { dias, app_id: appId })),
  })
}

export function useConsumoAgrupado(por: Agrupacion, dias: number, appId?: string) {
  return useQuery({
    queryKey: claves.agrupado(por, dias, appId),
    queryFn: () => request<Serie[]>(query(`/admin/usage/by/${por}`, { dias, app_id: appId })),
  })
}

export function useTraza(traceId: string) {
  return useQuery({
    queryKey: claves.traza(traceId),
    queryFn: () => request<PasoTraza[]>(`/admin/traces/${traceId}`),
    enabled: traceId !== "",
  })
}

// ---------------------------------------------------------------- catálogo

export function useAliases() {
  return useQuery({
    queryKey: claves.aliases(),
    queryFn: () => request<Alias[]>("/admin/aliases"),
  })
}

export function useGuardarAlias() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ alias, datos }: { alias: string; datos: GuardarAlias }) =>
      request<{ alias: string }>(`/admin/aliases/${alias}`, { method: "PUT", body: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.aliases() })
      // Un alias repuntado cambia a qué proveedor va el tráfico, así que el
      // desglose por proveedor deja de ser válido al instante.
      void qc.invalidateQueries({ queryKey: ["usage-by"] })
    },
  })
}

export function usePrecios(alias?: string) {
  return useQuery({
    queryKey: claves.precios(alias),
    queryFn: () => request<Precio[]>(query("/admin/prices", { alias })),
  })
}

/**
 * El histórico de la red de seguridad.
 *
 * Solo lectura: **no hay mutación para lanzar una reconciliación**. Sale a la API del
 * proveedor con la credencial del cliente, y eso se decide en el servidor, programado,
 * no con un botón que cualquiera con la consola abierta pueda pulsar en bucle.
 */
export function useReconciliaciones(limite = 50) {
  return useQuery({
    queryKey: claves.reconciliaciones(limite),
    queryFn: () => request<Reconciliacion[]>(query("/admin/reconciliations", { limite })),
  })
}

export function useCrearPrecio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (datos: CrearPrecio) =>
      request<{ price_id: string; alias: string }>("/admin/prices", {
        method: "POST",
        body: datos,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["prices"] }),
  })
}
